/**
 * F12 Section 5 — typed wrappers for the admin venue-photo moderation endpoints.
 *
 * Endpoints covered (all gated by `venue_photos:moderate`):
 *   GET  /admin/venue-photos?status&limit&offset   → paginated queue + total
 *   POST /admin/venue-photos/:photoId/approve      → flip to approved
 *   POST /admin/venue-photos/:photoId/reject       → flip to rejected (body: { reason })
 *
 * Response envelope is `{ data: VenuePhotoQueueResponse }` for the queue and
 * `{ data: VenuePhoto }` for the per-photo mutations. The helpers below unwrap
 * the envelope so callers receive the inner type.
 *
 * The queue row carries `{ photo, venueId, venueSlug, venueName, ownerUserId }`
 * exactly as the service returns it. The optional `variants` field on `photo`
 * is reserved for the post-Sharp-pipeline shape — at MVP the API ships only
 * `cdnUrl` and the editor falls back to it for both thumbnail and full-size.
 */
import type { VenuePhoto, VenuePhotoModerationStatus, VenuePhotoVariants } from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

// ─── Response envelopes ────────────────────────────────────────────────────────

/**
 * Optional Sharp-pipeline variants attached to a photo row. The MVP `VenuePhoto`
 * shape in `@bravemove/types` does not include this field; the API will start
 * returning it once the variant generator lands. Callers read with
 * `photo.variants?.heroUrl ?? photo.cdnUrl`.
 */
export type VenuePhotoWithVariants = VenuePhoto & {
  readonly variants?: VenuePhotoVariants | null;
};

export interface VenuePhotoQueueItem {
  readonly photo: VenuePhotoWithVariants;
  readonly venueId: string;
  readonly venueSlug: string;
  readonly venueName: string;
  readonly ownerUserId: string | null;
}

export interface VenuePhotoQueueResponse {
  readonly rows: readonly VenuePhotoQueueItem[];
  readonly total: number;
}

// ─── List options ──────────────────────────────────────────────────────────────

export interface VenuePhotoQueueOpts {
  readonly status?: VenuePhotoModerationStatus;
  readonly limit?: number;
  readonly offset?: number;
}

interface ApiEnvelope<T> {
  readonly data: T;
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminVenuePhotoApi = {
  /** GET /admin/venue-photos — paginated queue (FIFO via `created_at asc`). */
  listQueue: async (opts?: VenuePhotoQueueOpts): Promise<VenuePhotoQueueResponse> => {
    const body = await adminFetch<ApiEnvelope<VenuePhotoQueueResponse>>(
      `/admin/venue-photos${buildQuery({
        status: opts?.status,
        limit: opts?.limit,
        offset: opts?.offset,
      })}`,
    );
    return body.data;
  },

  /**
   * Fetch a single photo via the queue endpoint (offset-paged lookup by id).
   * The API does not expose a single-photo detail route; the review page reuses
   * the queue endpoint, scans the page for the requested id, and 404s if absent.
   * Status `'all'` is not a server-side filter — the helper iterates each known
   * status until the photo is found.
   */
  findOne: async (
    photoId: string,
  ): Promise<{ row: VenuePhotoQueueItem; status: VenuePhotoModerationStatus } | null> => {
    const STATUSES: readonly VenuePhotoModerationStatus[] = ['pending', 'approved', 'rejected'];
    for (const status of STATUSES) {
      let offset = 0;
      const limit = 100;
      // Walk pages of up to 1 000 rows per status — venues at MVP have <10 photos
      // and per-status totals stay well below this in practice.
      while (offset < 1000) {
        const page = await adminVenuePhotoApi.listQueue({ status, limit, offset });
        const found = page.rows.find((r) => r.photo.id === photoId);
        if (found) return { row: found, status };
        if (page.rows.length < limit) break;
        offset += limit;
      }
    }
    return null;
  },

  /** POST /admin/venue-photos/:photoId/approve. */
  approve: async (photoId: string): Promise<VenuePhoto> => {
    const body = await adminFetch<ApiEnvelope<VenuePhoto>>(
      `/admin/venue-photos/${photoId}/approve`,
      { method: 'POST' },
    );
    return body.data;
  },

  /** POST /admin/venue-photos/:photoId/reject — reason min 10 chars. */
  reject: async (photoId: string, reason: string): Promise<VenuePhoto> => {
    const body = await adminFetch<ApiEnvelope<VenuePhoto>>(
      `/admin/venue-photos/${photoId}/reject`,
      { method: 'POST', body: { reason } },
    );
    return body.data;
  },
};
